import React, { useState, useEffect, useMemo } from 'react';
import { useOutletContext } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Grid,
  CircularProgress,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  Tooltip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  Cell,
  LabelList,
} from 'recharts';
import { MdChevronLeft, MdChevronRight } from 'react-icons/md';
import theme, { conditionColor, conditionBg } from './theme';
import api from '../utils/api';
import { getClientEmail } from '../utils/getClientEmail';
import { sanitizeHtml } from '../utils/sanitizeHtml';
import {
  efficacyParamsFromHospital,
  efficacyParamsWithCalcMonth,
  efficacyMonthsParams,
} from './efficacyRequestParams';
import DashboardInsightCard from './DashboardInsightCard';
import SwabEvidenceTimeline from './SwabEvidenceTimeline';
import PathogenShieldGrid from './PathogenShieldGrid';
import EfficacyAuditDownload from './EfficacyAuditDownload';
import { swabGrowthPositive, trimSwabField } from './swabResultUtils';
import { buildProtectionMetrics, daySortKey } from './efficacyProtectionUtils';
import {
  addMonthsYYYYMM,
  currentYYYYMM,
  formatYYYYMMLong,
} from './efficacyMonthUtils';
import { EFFICACY_TERMS, SWAB_PAIR_EXPLAINER } from './efficacyTerminology';

/** Efficacy dashboard palette (matches client efficacy mockup — maroon for culture-positive / “before” emphasis). */
const EFFICACY_MAROON = '#8B2323';

/**
 * Unwraps AI/cache payloads (`surface: "efficacy"`, etc.) or `{ success, data }` envelopes.
 */
function normalizeSurfaceInsight(fromRes) {
  const raw = fromRes && typeof fromRes === 'object' && 'data' in fromRes ? fromRes.data : fromRes;
  if (raw == null || typeof raw !== 'object') return null;
  const payload = raw.success !== undefined && raw.data !== undefined ? raw.data : raw;
  if (!payload || typeof payload !== 'object') return null;
  const narrative = typeof payload.narrative === 'string' ? payload.narrative.trim() : '';
  const points = Array.isArray(payload.points) ? payload.points : [];
  const error = typeof payload.error === 'string' ? payload.error.trim() : '';
  if (!error && !narrative && points.length === 0) return null;
  return {
    narrative,
    points,
    error,
    fromCache: payload.fromCache ?? false,
    generatedAt: payload.generatedAt,
    cacheMode: payload.cacheMode,
    source: payload.source,
  };
}

/** Renders `**segments**` as bold (AI narrative markdown). */
function NarrativeWithBold({ text, sx }) {
  const parts = String(text).split(/(\*\*[^*]+\*\*)/g);
  return (
    <Typography component="div" sx={sx}>
      {parts.map((part, i) => {
        const m = part.match(/^\*\*([^*]+)\*\*$/);
        if (m) return <strong key={i}>{m[1]}</strong>;
        return part;
      })}
    </Typography>
  );
}

/** Swab JSON uses `specimen` (e.g. TABLE, PATIENT_BED) — normalize to one group key per site. */
function specimenGroupKeyFromRow(t) {
  const spec = trimSwabField(t.specimen);
  if (spec) return spec.toUpperCase().replace(/\s+/g, '_').replace(/_+/g, '_');
  const surf = trimSwabField(t.surface);
  if (surf) return `SRF:${surf.toUpperCase().replace(/\s+/g, '_')}`;
  const loc = trimSwabField(t.location);
  if (loc) return `LOC:${loc}`;
  const cat = trimSwabField(t.category);
  if (cat) return `CAT:${cat.toUpperCase().replace(/\s+/g, '_')}`;
  return 'OTHER';
}

/** Display like the efficacy mockup: "TABLE" → "Table", "PATIENT_BED" → "Patient bed". */
function formatSpecimenChartLabel(raw) {
  const s = trimSwabField(raw);
  if (!s) return 'Other';
  const spaced = s.replace(/_/g, ' ').replace(/\s+/g, ' ').trim().toLowerCase();
  if (!spaced) return 'Other';
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

function chartLabelForGroupKey(groupKey) {
  if (groupKey.startsWith('SRF:')) return formatSpecimenChartLabel(groupKey.slice(4).replace(/_/g, ' '));
  if (groupKey.startsWith('LOC:')) return groupKey.slice(4);
  if (groupKey.startsWith('CAT:')) return formatSpecimenChartLabel(groupKey.slice(4).replace(/_/g, ' '));
  if (groupKey === 'OTHER') return 'Other';
  return formatSpecimenChartLabel(groupKey.replace(/_/g, ' '));
}

const INVESTIGATION_DEFAULT_SUBTITLE = EFFICACY_TERMS.postUvcPositive.sectionSubtitle;

function mapResidualPanelEntry(e) {
  if (!e || typeof e !== 'object') return null;
  const title = trimSwabField(e.title || e.site || e.room || e.location || e.name) || 'Site';
  const datePart = trimSwabField(e.dateLabel || e.date);
  const pts = e.points ?? e.pointCount;
  const ptsLabel = pts != null && pts !== '' ? `${pts} point${Number(pts) === 1 ? '' : 's'}` : null;
  const metaRight =
    trimSwabField(e.metaRight) ||
    [datePart, ptsLabel].filter(Boolean).join(' · ') ||
    '—';
  const body = trimSwabField(e.body || e.description || e.notes || e.detail) || '';
  const resolution =
    trimSwabField(e.resolution || e.resolved || e.resolutionStatus || e.statusDetail) || '';
  return { title, metaRight, body, resolution };
}

/** Structured “investigation log” from API (`entries`, `badge`, `summary`, …). */
function parseResidualInvestigationPanel(raw) {
  if (raw == null) return null;
  const obj = typeof raw === 'object' && raw.success && raw.data != null ? raw.data : raw;
  if (!obj || typeof obj !== 'object') return null;
  const rawEntries = obj.entries || obj.log || obj.investigations || obj.cases;
  if (!Array.isArray(rawEntries) || rawEntries.length === 0) return null;
  const entries = rawEntries.map(mapResidualPanelEntry).filter(Boolean);
  if (entries.length === 0) return null;
  const summary = obj.summary && typeof obj.summary === 'object'
    ? {
        resultLine: trimSwabField(obj.summary.result || obj.summary.resultLine || obj.summary.headline),
        subLine: trimSwabField(obj.summary.sub || obj.summary.subLine || obj.summary.tagline),
      }
    : null;
  return {
    badge: trimSwabField(obj.badge || obj.badgeLabel) || null,
    subtitle: trimSwabField(obj.subtitle || obj.tagline) || null,
    entries,
    summary: summary?.resultLine || summary?.subLine ? summary : null,
  };
}

function monthChartLabel(iso) {
  if (!iso) return null;
  const d = new Date(`${iso}T12:00:00.000Z`);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('en-GB', { month: 'short', year: 'numeric' });
}

function KpiTile({ label, value, subtext, valueColor, helpText }) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.5,
        borderRadius: `${theme.radius.lg}px`,
        bgcolor: theme.colors.bgPrimary,
        border: `1px solid ${theme.colors.borderDefault}`,
        boxShadow: '0 1px 3px rgba(15, 23, 42, 0.06)',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
        <Typography
          sx={{
            fontSize: 11,
            fontWeight: 700,
            color: theme.colors.textSecondary,
            letterSpacing: '0.06em',
          }}
        >
          {label}
        </Typography>
        {helpText ? (
          <Tooltip title={helpText} arrow placement="top">
            <InfoOutlinedIcon
              sx={{ fontSize: 14, color: theme.colors.textSecondary, cursor: 'help' }}
              aria-label={`About ${label}`}
            />
          </Tooltip>
        ) : null}
      </Box>
      <Typography sx={{ fontSize: 32, fontWeight: 700, color: valueColor || theme.colors.textPrimary, lineHeight: 1.1 }}>
        {value}
      </Typography>
      {subtext && (
        <Typography sx={{ fontSize: 13, color: theme.colors.textSecondary, mt: 0.75 }}>{subtext}</Typography>
      )}
    </Paper>
  );
}

export default function EfficacyPage() {
  const { hospitalInfo, hasEfficacy, clientEmail } = useOutletContext();
  const [efficacyBaseParams, setEfficacyBaseParams] = useState(null);
  const [boundsLoading, setBoundsLoading] = useState(true);
  const [monthBounds, setMonthBounds] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(() => currentYYYYMM());
  const [monthPayload, setMonthPayload] = useState(null);
  const [monthLoading, setMonthLoading] = useState(false);
  const [dailyDetailOpen, setDailyDetailOpen] = useState(false);
  const [dailyDetailDateKey, setDailyDetailDateKey] = useState(null);
  const [dailyDetailTitle, setDailyDetailTitle] = useState('');

  const calcTz = useMemo(
    () => (typeof Intl !== 'undefined' ? Intl.DateTimeFormat().resolvedOptions().timeZone : 'UTC'),
    [],
  );

  useEffect(() => {
    const fetchMonths = async () => {
      setBoundsLoading(true);
      try {
        const email = clientEmail || getClientEmail();
        const params = efficacyParamsFromHospital(hospitalInfo, email);
        if (!params) {
          setEfficacyBaseParams(null);
          setMonthBounds(null);
          setMonthPayload(null);
          return;
        }
        setEfficacyBaseParams(params);
        const res = await api.get('/client/efficacy/months', { params: efficacyMonthsParams(params) });
        if (res.data?.success) {
          setMonthBounds(res.data);
          const def = res.data.defaultMonth || currentYYYYMM();
          setSelectedMonth((prev) => {
            const min = res.data.min;
            const max = res.data.max || def;
            const maxAllowed = max > currentYYYYMM() ? currentYYYYMM() : max;
            if (prev && min && prev >= min && prev <= maxAllowed) return prev;
            return def;
          });
        }
      } catch (e) {
        console.error('Efficacy months fetch error:', e);
      } finally {
        setBoundsLoading(false);
      }
    };
    fetchMonths();
  }, [hospitalInfo, clientEmail]);

  useEffect(() => {
    if (!efficacyBaseParams || !selectedMonth || boundsLoading) return undefined;

    let cancelled = false;
    const fetchMonthPayload = async () => {
      setMonthLoading(true);
      try {
        const monthParams = efficacyParamsWithCalcMonth(efficacyBaseParams, selectedMonth, calcTz);
        const res = await api.get('/client/efficacy', { params: monthParams });
        if (cancelled) return;
        if (res.data?.success) setMonthPayload(res.data);
        else setMonthPayload(null);
      } catch (e) {
        if (!cancelled) {
          console.error('Efficacy month fetch error:', e);
          setMonthPayload(null);
        }
      } finally {
        if (!cancelled) setMonthLoading(false);
      }
    };
    fetchMonthPayload();
    return () => {
      cancelled = true;
    };
  }, [efficacyBaseParams, selectedMonth, calcTz, boundsLoading]);

  const monthDerived = useMemo(() => {
    const swabTests = monthPayload?.swabTests || [];

    const bySurface = new Map();
    for (const t of swabTests) {
      const groupKey = specimenGroupKeyFromRow(t);
      const name = chartLabelForGroupKey(groupKey);
      if (!bySurface.has(groupKey)) bySurface.set(groupKey, { name, before: 0, after: 0 });
      const row = bySurface.get(groupKey);
      if (swabGrowthPositive(t.before)) row.before += 1;
      if (swabGrowthPositive(t.after)) row.after += 1;
    }
    const surfaceBars = [...bySurface.values()]
      .filter((r) => r.before > 0)
      .sort((a, b) => b.before - a.before)
      .slice(0, 8)
      .map((r) => ({
        ...r,
        beforeAfterLabel: `${r.before} → ${r.after}`,
        barColor: EFFICACY_MAROON,
      }));
    const allSurfaceCleared = surfaceBars.length > 0 && surfaceBars.every((r) => r.after === 0);

    const residualTests = swabTests.filter((t) => swabGrowthPositive(t.after ?? t.bacterialPost));
    const residualByKey = new Map();
    for (const t of residualTests) {
      const locRaw = trimSwabField(t.room) || trimSwabField(t.location);
      const loc = locRaw || 'Location not set';
      const key = `${loc}__${t.dateISO || t.date || ''}`;
      if (!residualByKey.has(key)) {
        residualByKey.set(key, {
          location: loc,
          dateISO: t.dateISO,
          dateLabel: trimSwabField(t.date) || '—',
          categories: new Set(),
          count: 0,
        });
      }
      const g = residualByKey.get(key);
      g.count += 1;
      const cat = trimSwabField(t.category) || trimSwabField(t.surface) || trimSwabField(t.specimen);
      if (cat) g.categories.add(cat);
    }
    const residualGroups = [...residualByKey.values()].sort((a, b) =>
      String(b.dateISO || '').localeCompare(String(a.dateISO || '')),
    );
    const residualMonthLabels = [...new Set(residualGroups.map((gr) => monthChartLabel(gr.dateISO)).filter(Boolean))];
    const residualBadge =
      residualGroups.length > 0
        ? `${residualTests.length} point${residualTests.length === 1 ? '' : 's'}${
            residualMonthLabels.length === 1 ? ` · ${String(residualMonthLabels[0]).split(' ')[0]} only` : ''
          }`
        : null;

    return {
      swabTests,
      surfaceBars,
      allSurfaceCleared,
      residualTests,
      residualGroups,
      residualBadge,
    };
  }, [monthPayload]);

  const scientificProofInsight = useMemo(() => {
    const d = monthPayload;
    return (
      normalizeSurfaceInsight({ data: d?.scientificProof }) ||
      normalizeSurfaceInsight({ data: d?.efficacyInsights }) ||
      normalizeSurfaceInsight({ data: d?.insightsFromView }) ||
      null
    );
  }, [monthPayload]);

  const monthAnalysis = monthPayload?.monthAnalysis ?? null;

  const residualInvestigationInsight = useMemo(() => {
    const d = monthPayload;
    return (
      normalizeSurfaceInsight({ data: d?.residualInvestigation }) ||
      normalizeSurfaceInsight({ data: d?.investigationInsights }) ||
      normalizeSurfaceInsight({ data: d?.residualInsights }) ||
      normalizeSurfaceInsight({ data: d?.investigationLog }) ||
      null
    );
  }, [monthPayload]);

  /** Structured AI payload only — swab roll-up lives on `monthDerived.residualGroups` (see `test.js`). */
  const investigationPanel = useMemo(() => {
    return (
      parseResidualInvestigationPanel(monthPayload?.residualInvestigationRaw) ||
      parseResidualInvestigationPanel(monthPayload?.residualInvestigation) ||
      parseResidualInvestigationPanel(monthPayload?.investigationLogPanel) ||
      null
    );
  }, [monthPayload]);

  const protectionMetrics = useMemo(() => {
    const residualCount = monthDerived.residualTests.length;
    let allResolved = residualCount === 0;
    if (residualCount > 0 && investigationPanel?.entries?.length) {
      allResolved = investigationPanel.entries.every((e) => Boolean(e.resolution));
    } else if (residualCount > 0) {
      allResolved = false;
    }
    const metrics = buildProtectionMetrics(
      monthDerived.swabTests,
      monthDerived.swabTests,
      monthPayload?.swabReports || [],
      selectedMonth,
      { residualCount, allResolved },
    );
    if (monthBounds?.lastResidual) {
      metrics.lastResidual = monthBounds.lastResidual;
    }
    return metrics;
  }, [monthDerived, monthPayload, selectedMonth, investigationPanel, monthBounds]);

  const monthAuditReports = monthPayload?.swabReports || [];

  const canGoBackMonth = monthBounds?.min ? selectedMonth > monthBounds.min : false;
  const canGoForwardMonth = selectedMonth < currentYYYYMM();
  const prevMonthLabel = useMemo(() => {
    if (!canGoBackMonth) return null;
    const prev = addMonthsYYYYMM(selectedMonth, -1);
    if (monthBounds?.min && prev < monthBounds.min) return formatYYYYMMLong(monthBounds.min);
    return formatYYYYMMLong(prev);
  }, [selectedMonth, monthBounds, canGoBackMonth]);
  const nextMonthLabel = useMemo(() => {
    if (!canGoForwardMonth) return null;
    const next = addMonthsYYYYMM(selectedMonth, 1);
    if (next > currentYYYYMM()) return null;
    return formatYYYYMMLong(next);
  }, [selectedMonth, canGoForwardMonth]);

  const dailyDetailSwabs = useMemo(() => {
    if (!dailyDetailDateKey) return [];
    return (monthPayload?.swabTests || []).filter((t) => {
      const key = daySortKey(t.dateISO) || daySortKey(t.date);
      return key === dailyDetailDateKey;
    });
  }, [monthPayload?.swabTests, dailyDetailDateKey]);

  const dailyDetailSummary = useMemo(() => {
    let posBefore = 0;
    let posAfter = 0;
    for (const t of dailyDetailSwabs) {
      if (swabGrowthPositive(t.before ?? t.bacterialPre)) posBefore += 1;
      if (swabGrowthPositive(t.after ?? t.bacterialPost)) posAfter += 1;
    }
    return { count: dailyDetailSwabs.length, posBefore, posAfter };
  }, [dailyDetailSwabs]);

  if (!hasEfficacy) {
    return (
      <Box sx={{ textAlign: 'center', py: 10 }}>
        <Typography sx={{ fontSize: 18, fontWeight: 600, color: theme.colors.textPrimary }}>Efficacy Testing Not Subscribed</Typography>
        <Typography sx={{ mt: 1, fontSize: 14, color: theme.colors.textSecondary }}>
          Contact your account manager to add swab testing to your plan.
        </Typography>
      </Box>
    );
  }

  if (boundsLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress sx={{ color: theme.colors.brandPrimary }} />
      </Box>
    );
  }

  const handleTimelineDotClick = (event) => {
    const dk = event?.dayKey;
    if (!dk) return;
    setDailyDetailDateKey(dk);
    const d = new Date(`${dk}T12:00:00`);
    setDailyDetailTitle(
      Number.isNaN(d.getTime())
        ? dk
        : d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' }),
    );
    setDailyDetailOpen(true);
  };

  const goPrevMonth = () => {
    if (!canGoBackMonth) return;
    setSelectedMonth((m) => {
      const prev = addMonthsYYYYMM(m, -1);
      if (monthBounds?.min && prev < monthBounds.min) return monthBounds.min;
      return prev;
    });
  };

  const goNextMonth = () => {
    if (!canGoForwardMonth) return;
    const next = addMonthsYYYYMM(selectedMonth, 1);
    if (next > currentYYYYMM()) return;
    setSelectedMonth(next);
  };

  const selectedMonthLabel = formatYYYYMMLong(selectedMonth);

  const fmtCell = (v) => {
    const s = v == null || v === '' ? '' : String(v).trim();
    return s && s !== '—' ? s : '—';
  };

  const sp = scientificProofInsight;
  const spPoints = sp?.points;
  const spHasPoints = Array.isArray(spPoints) && spPoints.length > 0;
  const spNarrative = sp?.narrative && String(sp.narrative).trim();
  const scientificProofHasAi = Boolean(spHasPoints || spNarrative);
  const scientificProofLegacy =
    !scientificProofHasAi && monthAnalysis && String(monthAnalysis).trim()
      ? String(monthAnalysis).trim()
      : null;

  const ri = residualInvestigationInsight;
  const riError = ri?.error;
  const riPoints = ri?.points;
  const riHasPoints = Array.isArray(riPoints) && riPoints.length > 0;
  const riNarrative = ri?.narrative && String(ri.narrative).trim();
  const hasApiInvestigationEntries = Boolean(investigationPanel?.entries?.length);
  const hasSwabResidualCards = monthDerived.residualGroups.length > 0;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <DashboardInsightCard
        title="Scientific proof of efficacy"
        insightsFromView={scientificProofInsight}
        legacyInsight={scientificProofLegacy}
        source={scientificProofHasAi ? 'ai' : 'template'}
        loading={monthLoading}
        showFreshnessChip
        disclaimerMode="short"
        attributionLive="Generated by AI from your current swab and efficacy data."
        attributionCached="Generated by AI from your swab and efficacy data."
        adminCacheInvalidation={
          efficacyBaseParams
            ? {
                surface: 'efficacy',
                cacheParams: {
                  ...efficacyBaseParams,
                  calcMonth: selectedMonth,
                  tz: calcTz,
                },
                onInvalidated: async () => {
                  if (!efficacyBaseParams || !selectedMonth) return;
                  setMonthLoading(true);
                  try {
                    const monthParams = efficacyParamsWithCalcMonth(
                      efficacyBaseParams,
                      selectedMonth,
                      calcTz
                    );
                    const res = await api.get('/client/efficacy', { params: monthParams });
                    if (res.data?.success) setMonthPayload(res.data);
                  } catch (e) {
                    console.error('Efficacy refetch error:', e);
                  } finally {
                    setMonthLoading(false);
                  }
                },
              }
            : undefined
        }
        sx={{ mb: 0 }}
      />

      <Box
        role="navigation"
        aria-label="Month navigator"
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 1.5,
          p: 2,
          borderRadius: `${theme.radius.lg}px`,
          border: `1px solid ${theme.colors.borderDefault}`,
          bgcolor: theme.colors.bgSecondary,
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: 11, fontWeight: 700, color: theme.colors.textSecondary, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
            Viewing month
          </Typography>
          <Typography sx={{ fontSize: 20, fontWeight: 700, color: theme.colors.textPrimary, lineHeight: 1.2 }}>
            {selectedMonthLabel || '—'}
          </Typography>
          <Typography sx={{ fontSize: 12, color: theme.colors.textSecondary, mt: 0.25 }}>
            Use the arrows to view another month — KPIs, timeline, and pathogen panel below update.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          {monthLoading ? (
            <CircularProgress size={22} sx={{ color: theme.colors.brandPrimary, mr: 0.5 }} />
          ) : null}
          <Button
            size="small"
            variant="outlined"
            startIcon={<MdChevronLeft size={18} />}
            disabled={!canGoBackMonth}
            onClick={goPrevMonth}
            aria-label={prevMonthLabel ? `Previous month: ${prevMonthLabel}` : 'Previous month'}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              borderColor: theme.colors.borderDefault,
              color: theme.colors.textPrimary,
              bgcolor: theme.colors.bgPrimary,
              '&:hover': { bgcolor: theme.colors.bgSecondary, borderColor: theme.colors.brandPrimary },
            }}
          >
            <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
              {prevMonthLabel || 'Previous'}
            </Box>
            <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>
              Prev
            </Box>
          </Button>
          <Button
            size="small"
            variant="outlined"
            endIcon={<MdChevronRight size={18} />}
            disabled={!canGoForwardMonth}
            onClick={goNextMonth}
            aria-label={nextMonthLabel ? `Next month: ${nextMonthLabel}` : 'Next month'}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              borderColor: theme.colors.borderDefault,
              color: theme.colors.textPrimary,
              bgcolor: theme.colors.bgPrimary,
              '&:hover': { bgcolor: theme.colors.bgSecondary, borderColor: theme.colors.brandPrimary },
            }}
          >
            <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
              {nextMonthLabel || 'Next'}
            </Box>
            <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>
              Next
            </Box>
          </Button>
        </Box>
      </Box>

      <Alert
        severity="info"
        variant="outlined"
        sx={{
          py: 0.75,
          fontSize: '0.8125rem',
          alignItems: 'flex-start',
          borderColor: theme.colors.borderDefault,
          bgcolor: theme.colors.bgSecondary,
          color: theme.colors.textPrimary,
          '& .MuiAlert-icon': { color: theme.colors.brandPrimary },
        }}
      >
        {SWAB_PAIR_EXPLAINER}
      </Alert>

      <Grid container spacing={2}>
        <Grid item xs={6} md={3}>
          <KpiTile
            label="PROTECTED SURFACE-DAYS"
            value={protectionMetrics.protectedSurfaceDays}
            subtext="cleared post-UVC"
            valueColor={theme.colors.accentSuccess}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <KpiTile
            label="CLEAN SWAB EVENTS"
            value={protectionMetrics.cleanSwabEvents}
            subtext={
              protectionMetrics.reportCount > 0
                ? `of ${protectionMetrics.reportCount} report${protectionMetrics.reportCount === 1 ? '' : 's'}`
                : '—'
            }
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <KpiTile
            label="PATHOGENS SCREENED"
            value={protectionMetrics.pathogenCount || '—'}
            subtext="HAI panel"
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <KpiTile
            label={EFFICACY_TERMS.postUvcPositive.kpiLabel}
            value={protectionMetrics.residualCount}
            subtext={protectionMetrics.residualsSubtitle}
            helpText={EFFICACY_TERMS.postUvcPositive.kpiHelp}
            valueColor={protectionMetrics.residualCount > 0 ? theme.colors.accentWarning : theme.colors.textPrimary}
          />
        </Grid>
      </Grid>

      <SwabEvidenceTimeline
        timelineWeeks={protectionMetrics.timelineWeeks}
        reportCount={protectionMetrics.reportCount}
        monthLabel={selectedMonthLabel}
        streak={protectionMetrics.streak}
        lastResidual={protectionMetrics.lastResidual}
        onDotClick={handleTimelineDotClick}
      />

      <PathogenShieldGrid
        pathogenCells={protectionMetrics.pathogenCells}
        pathogenCount={protectionMetrics.pathogenCount}
      />


      {/* Bottom row */}
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Paper
            elevation={0}
            sx={{
              p: 2.5,
              height: '100%',
              borderRadius: `${theme.radius.lg}px`,
              border: `1px solid ${theme.colors.borderDefault}`,
              boxShadow: '0 1px 3px rgba(15, 23, 42, 0.06)',
            }}
          >
            <Typography sx={{ fontSize: 16, fontWeight: 700, color: theme.colors.textPrimary, mb: 2 }}>
              High-touch surfaces — contamination cleared
            </Typography>
            {monthDerived.surfaceBars.length === 0 ? (
              <Box sx={{ minHeight: 220, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: theme.colors.bgSecondary, borderRadius: 2 }}>
                <Typography sx={{ fontSize: 14, color: theme.colors.textSecondary, px: 2, textAlign: 'center' }}>
                  No positive-before surface groups in the current data.
                </Typography>
              </Box>
            ) : (
              <ResponsiveContainer width="100%" height={Math.max(220, monthDerived.surfaceBars.length * 36)}>
                <BarChart
                  layout="vertical"
                  data={monthDerived.surfaceBars}
                  margin={{ top: 4, right: 24, left: 4, bottom: 4 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke={theme.colors.borderDefault} horizontal={false} />
                  <XAxis type="number" tick={{ fontSize: 11, fill: theme.colors.textSecondary }} allowDecimals={false} />
                  <YAxis
                    type="category"
                    dataKey="name"
                    width={120}
                    tick={{ fontSize: 11, fill: theme.colors.textPrimary }}
                    tickFormatter={(v) => (String(v).length > 16 ? `${String(v).slice(0, 14)}…` : v)}
                  />
                  <RechartsTooltip
                    formatter={(_, __, props) => [`${props.payload.before} → ${props.payload.after}`, 'Positive before → after']}
                    labelFormatter={(label) => label}
                  />
                  <Bar dataKey="before" radius={[0, 4, 4, 0]} barSize={18}>
                    {monthDerived.surfaceBars.map((entry, index) => (
                      <Cell key={`surface-bar-${entry.name}-${index}`} fill={entry.barColor} />
                    ))}
                    <LabelList
                      dataKey="beforeAfterLabel"
                      position="right"
                      style={{ fill: theme.colors.textSecondary, fontSize: 12, fontWeight: 500 }}
                    />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
            <Typography sx={{ fontSize: 12, color: theme.colors.textSecondary, mt: 1.5 }}>
              {monthDerived.allSurfaceCleared
                ? 'All bacterial positives on these surfaces were eliminated post-UVC.'
                : 'Each surface uses its own color for positive detections before UVC; labels show before → after counts.'}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper
            elevation={0}
            sx={{
              p: 2.5,
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0,
              borderRadius: `${theme.radius.lg}px`,
              border: `1px solid ${theme.colors.borderDefault}`,
              boxShadow: '0 1px 3px rgba(15, 23, 42, 0.06)',
            }}
          >
            <Box sx={{ flexShrink: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 0.5 }}>
                <Typography sx={{ fontSize: 16, fontWeight: 700, color: theme.colors.textPrimary }}>
                  {EFFICACY_TERMS.postUvcPositive.sectionTitle}
                </Typography>
                {(investigationPanel?.badge || monthDerived.residualBadge) && (
                  <Chip
                    label={investigationPanel?.badge || monthDerived.residualBadge}
                    size="small"
                    sx={{
                      fontWeight: 600,
                      fontSize: 11,
                      bgcolor: '#fff7ed',
                      color: '#9a3412',
                      border: '1px solid rgba(234, 88, 12, 0.25)',
                    }}
                  />
                )}
              </Box>
              <Typography sx={{ fontSize: 13, color: theme.colors.textSecondary, lineHeight: 1.55, mb: 2 }}>
                {investigationPanel?.subtitle || INVESTIGATION_DEFAULT_SUBTITLE}
              </Typography>
            </Box>

            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                maxHeight: { xs: '42vh', sm: '48vh', md: 'min(52vh, 520px)' },
                overflowY: 'auto',
                overflowX: 'hidden',
                pr: 0.5,
                mr: -0.5,
                scrollbarGutter: 'stable',
                scrollbarWidth: 'thin',
                WebkitOverflowScrolling: 'touch',
                '&::-webkit-scrollbar': { width: 8 },
                '&::-webkit-scrollbar-thumb': {
                  borderRadius: 4,
                  bgcolor: 'rgba(15, 23, 42, 0.18)',
                },
                '&::-webkit-scrollbar-thumb:hover': { bgcolor: 'rgba(15, 23, 42, 0.28)' },
              }}
            >
              {riError ? (
                <Alert
                  severity="warning"
                  variant="outlined"
                  sx={{
                    mb: 1.5,
                    py: 0.5,
                    fontSize: '0.8125rem',
                    alignItems: 'flex-start',
                    borderColor: theme.colors.borderDefault,
                    bgcolor: 'rgba(248, 113, 113, 0.06)',
                    color: theme.colors.textPrimary,
                    '& .MuiAlert-icon': { color: theme.colors.accentWarning },
                  }}
                >
                  {riError}
                </Alert>
              ) : null}

              {hasApiInvestigationEntries ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {investigationPanel.entries.map((entry, idx) => (
                  <Paper
                    key={`${entry.title}-${idx}`}
                    elevation={0}
                    sx={{
                      p: 2,
                      borderRadius: '10px',
                      border: '1px solid rgba(139, 35, 35, 0.12)',
                      borderLeft: `4px solid ${EFFICACY_MAROON}`,
                      bgcolor: '#fafaf9',
                    }}
                  >
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'baseline', gap: 1 }}>
                      <Typography sx={{ fontSize: 15, fontWeight: 700, color: theme.colors.textPrimary }}>{entry.title}</Typography>
                      <Typography sx={{ fontSize: 13, fontWeight: 600, color: theme.colors.textSecondary }}>{entry.metaRight}</Typography>
                    </Box>
                    {entry.body ? (
                      <Typography sx={{ fontSize: 13, color: theme.colors.textSecondary, lineHeight: 1.6, mt: 1 }}>
                        {entry.body}
                      </Typography>
                    ) : null}
                    {entry.resolution ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 1.5 }}>
                        <CheckCircleIcon sx={{ fontSize: 20, color: theme.colors.accentSuccess, flexShrink: 0 }} />
                        <Typography sx={{ fontSize: 13, fontWeight: 600, color: theme.colors.accentSuccess, lineHeight: 1.45 }}>
                          {entry.resolution}
                        </Typography>
                      </Box>
                    ) : null}
                  </Paper>
                ))}
              </Box>
            ) : hasSwabResidualCards ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {monthDerived.residualGroups.slice(0, 6).map((g, idx) => (
                  <Paper
                    key={`${g.location}-${g.dateISO}-${idx}`}
                    elevation={0}
                    sx={{
                      p: 2,
                      borderRadius: `${theme.radius.md}px`,
                      border: `1px solid ${theme.colors.borderDefault}`,
                      bgcolor: theme.colors.bgPrimary,
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
                      <Typography sx={{ fontSize: 14, fontWeight: 700, color: theme.colors.textPrimary }}>{g.location}</Typography>
                      <Typography sx={{ fontSize: 13, color: theme.colors.textSecondary }}>
                        {g.dateLabel} · {g.count} point{g.count === 1 ? '' : 's'}
                      </Typography>
                    </Box>
                    <Typography sx={{ fontSize: 13, color: theme.colors.textSecondary, mt: 1, lineHeight: 1.5 }}>
                      {g.categories.size
                        ? `Surfaces: ${[...g.categories].slice(0, 5).join(', ')}${g.categories.size > 5 ? '…' : ''}`
                        : EFFICACY_TERMS.postUvcPositive.cardBody}
                    </Typography>
                    <Typography sx={{ fontSize: 13, fontWeight: 600, color: theme.colors.accentSuccess, mt: 1 }}>
                      ✓ Follow up via repeat swab / protocol check — see linked lab report for details
                    </Typography>
                  </Paper>
                ))}
              </Box>
            ) : riHasPoints ? (
              <Box
                component="ul"
                sx={{
                  m: 0,
                  pl: 2.25,
                  color: theme.colors.textPrimary,
                  py: 1,
                  borderRadius: `${theme.radius.md}px`,
                  border: `1px dashed ${theme.colors.borderDefault}`,
                  bgcolor: theme.colors.bgSecondary,
                  '& li': { fontSize: '0.875rem', fontWeight: 400, lineHeight: 1.65, mb: 0.75, pl: 0.25 },
                  '& li:last-of-type': { mb: 0 },
                  '& b': { fontWeight: 700 },
                }}
              >
                {riPoints.map((html, idx) => (
                  <Box component="li" key={idx} dangerouslySetInnerHTML={{ __html: sanitizeHtml(html) }} />
                ))}
              </Box>
            ) : riNarrative ? (
              <Box
                sx={{
                  py: 2,
                  px: 2,
                  borderRadius: `${theme.radius.md}px`,
                  border: `1px solid ${theme.colors.borderDefault}`,
                  bgcolor: theme.colors.bgSecondary,
                }}
              >
                <NarrativeWithBold
                  text={riNarrative}
                  sx={{ fontSize: 14, fontWeight: 500, color: theme.colors.textPrimary, lineHeight: 1.65, textAlign: 'left' }}
                />
              </Box>
            ) : (
              <Box
                sx={{
                  py: 4,
                  px: 2,
                  textAlign: 'center',
                  borderRadius: `${theme.radius.md}px`,
                  border: `1px dashed ${theme.colors.borderDefault}`,
                  bgcolor: theme.colors.bgSecondary,
                }}
              >
                <Typography sx={{ fontSize: 14, color: theme.colors.textSecondary }}>
                  {EFFICACY_TERMS.postUvcPositive.emptyState}
                </Typography>
              </Box>
            )}
            </Box>

            <Paper
              elevation={0}
              sx={{
                flexShrink: 0,
                mt: 2,
                p: 2,
                borderRadius: `${theme.radius.md}px`,
                bgcolor: '#ecfdf5',
                border: '1px solid rgba(5, 150, 105, 0.15)',
              }}
            >
              <Typography sx={{ fontSize: 14, fontWeight: 600, color: '#14532d' }}>
                {investigationPanel?.summary?.resultLine ||
                  (monthDerived.residualTests.length === 0
                    ? EFFICACY_TERMS.postUvcPositive.resultZero
                    : EFFICACY_TERMS.postUvcPositive.resultSome)}
              </Typography>
              <Typography sx={{ fontSize: 13, color: theme.colors.textSecondary, mt: 0.5 }}>
                {investigationPanel?.summary?.subLine || 'The process learned and improved'}
              </Typography>
            </Paper>
          </Paper>
        </Grid>
      </Grid>

      <EfficacyAuditDownload reports={monthAuditReports} monthLabel={selectedMonthLabel} />

      <Dialog
        open={dailyDetailOpen}
        onClose={() => setDailyDetailOpen(false)}
        maxWidth="lg"
        fullWidth
        scroll="paper"
        PaperProps={{
          sx: { borderRadius: `${theme.radius.lg}px`, maxHeight: 'min(90vh, 720px)' },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 2,
            pr: 1,
            py: 2,
            borderBottom: `1px solid ${theme.colors.borderDefault}`,
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: 11, fontWeight: 700, color: theme.colors.textSecondary, letterSpacing: '0.06em', mb: 0.5 }}>
              SWAB TESTS FOR DATE
            </Typography>
            <Typography sx={{ fontSize: 18, fontWeight: 700, color: theme.colors.textPrimary, lineHeight: 1.3 }}>
              {dailyDetailTitle || dailyDetailDateKey || '—'}
            </Typography>
            {dailyDetailSummary.count > 0 && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1.5 }}>
                <Chip size="small" label={`${dailyDetailSummary.count} point${dailyDetailSummary.count === 1 ? '' : 's'}`} sx={{ fontWeight: 600 }} />
                <Chip
                  size="small"
                  label={`${dailyDetailSummary.posBefore} positive before UVC`}
                  sx={{ fontWeight: 600, bgcolor: conditionBg('danger'), color: conditionColor('danger') }}
                />
                <Chip
                  size="small"
                  label={`${dailyDetailSummary.posAfter} positive after UVC`}
                  sx={{ fontWeight: 600, bgcolor: conditionBg('success'), color: conditionColor('success') }}
                />
              </Box>
            )}
          </Box>
          <IconButton aria-label="Close" onClick={() => setDailyDetailOpen(false)} sx={{ mt: -0.5 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ px: 0 }}>
          {dailyDetailSwabs.length === 0 ? (
            <Box sx={{ px: 3, py: 4 }}>
              <Typography sx={{ fontSize: 14, color: theme.colors.textSecondary }}>No swab rows for this date.</Typography>
            </Box>
          ) : (
            <TableContainer sx={{ maxHeight: 'min(55vh, 480px)' }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    {['#', 'Room', 'Category', 'Surface', 'Specimen', 'Before', 'After', 'Reduction %', 'Status'].map((h) => (
                      <TableCell
                        key={h}
                        sx={{
                          bgcolor: theme.colors.textPrimary,
                          color: '#fff',
                          fontWeight: 600,
                          fontSize: 11,
                          whiteSpace: 'nowrap',
                          ...(h === 'Before' || h === 'After' ? { minWidth: 88 } : {}),
                        }}
                      >
                        {h}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {dailyDetailSwabs.map((t, idx) => (
                    <TableRow key={`${t.dateISO || ''}-${t.location || ''}-${idx}`} sx={{ '&:nth-of-type(even)': { bgcolor: theme.colors.bgSecondary } }}>
                      <TableCell sx={{ fontSize: 12, fontWeight: 600 }}>{idx + 1}</TableCell>
                      <TableCell sx={{ fontSize: 12, fontWeight: 600, maxWidth: 140 }}>{fmtCell(t.location || t.room)}</TableCell>
                      <TableCell sx={{ fontSize: 12, maxWidth: 120, wordBreak: 'break-word' }}>{fmtCell(t.category)}</TableCell>
                      <TableCell sx={{ fontSize: 12, maxWidth: 120, wordBreak: 'break-word' }}>{fmtCell(t.surface)}</TableCell>
                      <TableCell sx={{ fontSize: 12, maxWidth: 120, wordBreak: 'break-word' }}>{fmtCell(t.specimen)}</TableCell>
                      <TableCell sx={{ fontSize: 12, maxWidth: 160, wordBreak: 'break-word' }}>{fmtCell(t.before ?? t.bacterialPre)}</TableCell>
                      <TableCell sx={{ fontSize: 12, maxWidth: 160, wordBreak: 'break-word' }}>{fmtCell(t.after ?? t.bacterialPost)}</TableCell>
                      <TableCell sx={{ fontSize: 12 }}>{fmtCell(t.reductionPct)}</TableCell>
                      <TableCell sx={{ fontSize: 12 }}>{fmtCell(t.status)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2.5, py: 2, borderTop: `1px solid ${theme.colors.borderDefault}` }}>
          <Button variant="contained" onClick={() => setDailyDetailOpen(false)} sx={{ textTransform: 'none', fontWeight: 600 }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
